/**
 * `/admin/users/:id` — one user (SPEC §9.1).
 *
 * "Roles held (with source), and a read-only **effective access** panel — the
 * resolved department and report list, per §7.2."
 *
 * The panel comes down with this response, resolved server-side and uncached,
 * so it always agrees with the roles shown above it — including immediately
 * after an edit, which is when an administrator is most likely to be reading
 * it. See `portal/admin/service.py`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'

import { EffectiveAccessPanel } from '../../components/admin/EffectiveAccessPanel'
import { Badge, Button, Card } from '../../components/admin/ui'
import { ErrorState, Skeleton } from '../../components/States'
import { adminApi } from '../../lib/admin'
import type { AdminUserDetail as UserDetail } from '../../lib/adminTypes'
import { formatDateTime } from '../../lib/format'

function RoleList({
  detail,
  onChange,
  saving,
}: {
  detail: UserDetail
  onChange: (roleIds: readonly number[]) => void
  saving: boolean
}) {
  const roles = useQuery({ queryKey: ['admin', 'roles'], queryFn: adminApi.roles })
  const held = new Set(detail.roles.map((role) => role.id))
  const bySource = new Map(detail.roles.map((role) => [role.id, role]))

  if (roles.isPending) return <Skeleton rows={3} />
  if (roles.error !== null) return <ErrorState error={roles.error} />

  return (
    <ul className="space-y-1.5">
      {roles.data.map((role) => {
        const assignment = bySource.get(role.id)
        const fromGroup = assignment?.source === 'entra_group'
        return (
          <li key={role.id} className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-[length:var(--text-sm)]">
              <input
                type="checkbox"
                checked={held.has(role.id)}
                disabled={saving || fromGroup}
                onChange={() => {
                  const next = held.has(role.id)
                    ? [...held].filter((id) => id !== role.id)
                    : [...held, role.id]
                  onChange(next)
                }}
              />
              {role.name}
              <span className="numeric text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
                {role.key}
              </span>
            </label>
            {fromGroup && (
              // §7.3: group-derived roles are read-only and labelled with the
              // group, so an admin can see why unticking one would not stick.
              <Badge tone="warning">from group {assignment.source_groups.join(', ')}</Badge>
            )}
            {assignment?.granted_by_upn !== undefined && assignment.granted_by_upn !== null && (
              <span className="text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
                granted by {assignment.granted_by_upn}
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

export function AdminUserDetail() {
  const { id = '' } = useParams()
  const queryClient = useQueryClient()

  const detail = useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: () => adminApi.user(id),
  })

  function onSaved(updated: UserDetail): void {
    queryClient.setQueryData(['admin', 'user', id], updated)
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    // The signed-in administrator's own access can change here too.
    void queryClient.invalidateQueries({ queryKey: ['me'] })
  }

  const setRoles = useMutation({
    mutationFn: (roleIds: readonly number[]) => adminApi.setUserRoles(id, roleIds),
    onSuccess: onSaved,
  })
  const patch = useMutation({
    mutationFn: (body: { is_active?: boolean; is_admin?: boolean }) =>
      adminApi.patchUser(id, body),
    onSuccess: onSaved,
  })

  if (detail.isPending) return <Skeleton rows={6} />
  if (detail.error !== null)
    return (
      <ErrorState
        error={detail.error}
        onRetry={() => {
          void detail.refetch()
        }}
      />
    )

  const user = detail.data

  return (
    <div className="space-y-4">
      <Card
        title={user.display_name}
        description={user.upn}
        actions={
          <Link
            to="/admin/users"
            className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
          >
            Back to users
          </Link>
        }
      >
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[length:var(--text-sm)] sm:grid-cols-4">
          <div>
            <dt className="text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">Email</dt>
            <dd>{user.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
              First seen
            </dt>
            <dd className="numeric">{formatDateTime(user.created_at)}</dd>
          </div>
          <div>
            <dt className="text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
              Last sign-in
            </dt>
            <dd className="numeric">
              {user.last_login_at === null ? '—' : formatDateTime(user.last_login_at)}
            </dd>
          </div>
          <div>
            <dt className="text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">Status</dt>
            <dd className="flex gap-1">
              {user.is_admin && <Badge tone="positive">administrator</Badge>}
              <Badge tone={user.is_active ? 'neutral' : 'danger'}>
                {user.is_active ? 'active' : 'deactivated'}
              </Badge>
            </dd>
          </div>
        </dl>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            disabled={patch.isPending}
            onClick={() => {
              patch.mutate({ is_active: !user.is_active })
            }}
          >
            {user.is_active ? 'Deactivate' : 'Reactivate'}
          </Button>
          <Button
            disabled={patch.isPending}
            onClick={() => {
              patch.mutate({ is_admin: !user.is_admin })
            }}
          >
            {user.is_admin ? 'Remove administrator' : 'Make administrator'}
          </Button>
        </div>
        {patch.error !== null && (
          <div className="mt-3">
            <ErrorState error={patch.error} />
          </div>
        )}
      </Card>

      <Card
        title="Roles"
        description="Ticking a role saves immediately. Roles are replaced wholesale."
      >
        <RoleList
          detail={user}
          saving={setRoles.isPending}
          onChange={(roleIds) => {
            setRoles.mutate(roleIds)
          }}
        />
        {setRoles.error !== null && (
          <div className="mt-3">
            <ErrorState error={setRoles.error} />
          </div>
        )}
      </Card>

      <Card
        title="Effective access"
        description="Resolved server-side, by the same code the report endpoints use. Read-only."
      >
        <EffectiveAccessPanel panel={user.effective_access} />
      </Card>
    </div>
  )
}
